import { type KeyboardEvent } from 'react';
import { careModes, type CareModeId } from '../content';
import { PetCanvas } from '../components/PetCanvas';
import { useCareMode } from '../context/CareModeContext';
import { MagneticCta } from '../components/MagneticCta';
import { siteConfig } from '../../../shared/config/site';
import { careModePetIds, petAssetById } from '../petAssets';

export function CareModesSection() {
  const { modeId, setModeId, mode } = useCareMode();

  const handleModeSelect = (id: CareModeId) => {
    setModeId(id);
  };

  // Roving focus: a tablist is driven with arrow keys, not Tab.
  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 0;

    if (step === 0) {
      return;
    }

    event.preventDefault();
    const current = careModes.findIndex((careMode) => careMode.id === modeId);
    const next = careModes[(current + step + careModes.length) % careModes.length];
    setModeId(next.id);
    document.getElementById(`mode-tab-${next.id}`)?.focus();
  };

  const ctaHref = modeId === 'urgent' ? siteConfig.emergencyHref : siteConfig.phoneHref;
  const ctaLabel = modeId === 'urgent' ? 'Call emergency' : 'Call the clinic';

  return (
    <section
      id="modes"
      className="relative overflow-hidden py-[var(--section-padding-y)]"
      aria-labelledby="modes-heading"
      style={{
        background: `linear-gradient(135deg, ${mode.fog}22, #e8efeb 45%, ${mode.accent}18)`,
      }}
    >
      <div className="mx-auto max-w-[var(--container-max)] px-[var(--section-padding-x)]">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="section-eyebrow">Visit type</p>
            <h2 id="modes-heading" className="section-title">
              Dogs, cats or after-hours
            </h2>
            <p className="section-copy max-w-lg">
              Pick the visit you need. The number you call changes with it.
            </p>

            <div className="mt-10 flex flex-col gap-2" role="tablist" aria-label="Visit type">
              {careModes.map((careMode) => {
                const isSelected = modeId === careMode.id;

                return (
                  <button
                    key={careMode.id}
                    type="button"
                    role="tab"
                    id={`mode-tab-${careMode.id}`}
                    aria-selected={isSelected}
                    aria-controls="mode-stage"
                    tabIndex={isSelected ? 0 : -1}
                    onKeyDown={handleTabKeyDown}
                    onClick={() => handleModeSelect(careMode.id)}
                    className={`min-h-14 border-l-[3px] px-5 py-4 text-left transition-[background-color,border-color,color] duration-200 ease-in-out ${
                      isSelected
                        ? 'bg-white/90 text-ink shadow-md'
                        : 'border-l-neutral-300 text-neutral-600 hover:border-l-neutral-400 hover:bg-white/40 hover:text-ink'
                    }`}
                    style={isSelected ? { borderLeftColor: careMode.light } : undefined}
                  >
                    <span className="block font-display text-lg font-semibold">{careMode.label}</span>
                    <span className="mt-1 block font-body text-[1.0625rem] leading-6 text-neutral-600">{careMode.blurb}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <MagneticCta href={ctaHref} variant="primary">
                {ctaLabel}
              </MagneticCta>
              <p className="font-body text-sm text-neutral-500" aria-live="polite">
                Active: {mode.label}
              </p>
            </div>
          </div>

          <div
            id="mode-stage"
            role="tabpanel"
            aria-labelledby={`mode-tab-${modeId}`}
            className="stage-frame aspect-square w-full max-w-lg justify-self-center"
            style={{ boxShadow: `0 28px 60px ${mode.fog}66` }}
          >
            <div
              className="absolute inset-0"
              style={{
                background: `radial-gradient(ellipse 70% 55% at 50% 110%, ${mode.accent}33, transparent 55%), radial-gradient(ellipse 50% 40% at 80% 15%, ${mode.light}44, transparent 50%), ${mode.fog}`,
              }}
              aria-hidden="true"
            />
            {/* The canvas is an enhancement: it waits for interaction and never
                arrives at all without WebGL. The stage needs its subject either
                way, so a plain cutout carries it and the scene layers on top.
                Plain, not the parallax cutout: that one draws the image twice
                with a blur and a drop shadow, which is raster time this stage
                spends entirely behind the canvas. */}
            <img
              src={petAssetById[careModePetIds[modeId]].srcSm}
              alt=""
              width={petAssetById[careModePetIds[modeId]].width}
              height={petAssetById[careModePetIds[modeId]].height}
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              className="pointer-events-none absolute inset-0 z-[1] m-auto h-[86%] w-auto object-contain"
            />
            <div className="film-grain z-[2]" aria-hidden="true" />
            <PetCanvas
              className="z-[3]"
              compact
              pointerEnabled
              textureSrc={petAssetById[careModePetIds[modeId]].src}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
